"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { Button } from '@open-mercato/ui/primitives/button'

const WAGON_TYPE_OPTIONS = [
  { value: 'flat', label: 'Flat' },
  { value: 'tank', label: 'Tank' },
  { value: 'hopper', label: 'Hopper' },
  { value: 'box', label: 'Box' },
  { value: 'intermodal', label: 'Intermodal' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

type WagonRow = {
  id: string
  evn: string
  name: string
  series: string
  wagon_type: string
  status: string
  weight_tonnes: number
  max_load_tonnes: number | null
  ecm_name: string
  ecm_valid_until: string | null
  created_at: string
}

type ResponsePayload = {
  items: WagonRow[]
  total: number
  page: number
  totalPages: number
}

export default function WagonsPage() {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'rolling-stock-wagons-list',
  })

  const [rows, setRows] = React.useState<WagonRow[]>([])
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)

  const pageSize = 50

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search) params.set('evn', search)
    if (filterValues.wagonType) params.set('wagonType', String(filterValues.wagonType))
    if (filterValues.status) params.set('status', String(filterValues.status))
    if (filterValues.series) params.set('series', String(filterValues.series))
    return params.toString()
  }, [page, search, filterValues])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      const fallback: ResponsePayload = { items: [], total: 0, page, totalPages: 1 }
      const call = await apiCall<ResponsePayload>(
        `/api/rolling-stock/wagons?${queryParams}`,
        undefined,
        { fallback }
      )
      if (cancelled) return
      if (!call.ok) {
        flash(t('rolling_stock.wagons.list.error.load', 'Failed to load wagons'), 'error')
        setIsLoading(false)
        return
      }
      const payload = call.result ?? fallback
      setRows(payload.items)
      setTotal(payload.total)
      setTotalPages(payload.totalPages)
      setIsLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleDelete = React.useCallback(async (row: WagonRow) => {
    const confirmed = await confirm({
      title: t('rolling_stock.wagons.delete.confirm.title', 'Delete wagon?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow('/api/rolling-stock/wagons', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.id }),
        }, { errorMessage: t('rolling_stock.wagons.flash.delete_error', 'Failed to delete wagon') }),
        context: {},
      })
      flash(t('rolling_stock.wagons.flash.deleted', 'Wagon deleted'), 'success')
      setReloadToken((v) => v + 1)
    } catch {
      flash(t('rolling_stock.wagons.flash.delete_error', 'Failed to delete wagon'), 'error')
    }
  }, [confirm, runMutation, t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'wagonType',
      label: t('rolling_stock.wagons.filters.wagonType', 'Wagon Type'),
      type: 'select',
      options: WAGON_TYPE_OPTIONS,
    },
    {
      id: 'status',
      label: t('rolling_stock.wagons.filters.status', 'Status'),
      type: 'select',
      options: STATUS_OPTIONS,
    },
    {
      id: 'series',
      label: t('rolling_stock.wagons.filters.series', 'Series'),
      type: 'text',
    },
  ], [t])

  const columns = React.useMemo<ColumnDef<WagonRow>[]>(() => [
    {
      accessorKey: 'evn',
      header: t('rolling_stock.wagons.columns.evn', 'EVN'),
      cell: ({ row }) => (
        <Link href={`/backend/rolling-stock/wagons/${row.original.id}`} className="font-mono text-sm">
          {row.original.evn}
        </Link>
      ),
    },
    {
      accessorKey: 'name',
      header: t('rolling_stock.wagons.columns.name', 'Name'),
    },
    {
      accessorKey: 'series',
      header: t('rolling_stock.wagons.columns.series', 'Series'),
    },
    {
      accessorKey: 'wagon_type',
      header: t('rolling_stock.wagons.columns.wagonType', 'Type'),
      cell: ({ getValue }) => {
        const val = getValue<string>()
        return WAGON_TYPE_OPTIONS.find((o) => o.value === val)?.label ?? val
      },
    },
    {
      accessorKey: 'status',
      header: t('rolling_stock.wagons.columns.status', 'Status'),
      cell: ({ getValue }) => {
        const val = getValue<string>()
        const colorMap: Record<string, string> = {
          available: 'text-green-600',
          maintenance: 'text-amber-600',
          in_use: 'text-blue-600',
        }
        const label = STATUS_OPTIONS.find((o) => o.value === val)?.label ?? val
        return <span className={colorMap[val] ?? ''}>{label}</span>
      },
    },
    {
      accessorKey: 'weight_tonnes',
      header: t('rolling_stock.wagons.columns.weightTonnes', 'Weight (t)'),
    },
    {
      accessorKey: 'max_load_tonnes',
      header: t('rolling_stock.wagons.columns.maxLoadTonnes', 'Max Load (t)'),
      cell: ({ getValue }) => getValue<number | null>() ?? '—',
    },
    {
      accessorKey: 'ecm_name',
      header: t('rolling_stock.wagons.columns.ecmName', 'ECM'),
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <DataTable<WagonRow>
          title={t('rolling_stock.wagons.list.title', 'Wagons')}
          actions={
            <Button asChild>
              <Link href="/backend/rolling-stock/wagons/create">
                {t('rolling_stock.wagons.list.add', 'Add Wagon')}
              </Link>
            </Button>
          }
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder={t('rolling_stock.wagons.list.searchPlaceholder', 'Search by EVN…')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(values) => { setFilterValues(values); setPage(1) }}
          onFiltersClear={() => { setFilterValues({}); setPage(1) }}
          onRowClick={(row) => router.push(`/backend/rolling-stock/wagons/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                { id: 'edit', label: t('common.edit', 'Edit'), href: `/backend/rolling-stock/wagons/${row.id}` },
                { id: 'delete', label: t('common.delete', 'Delete'), destructive: true, onSelect: () => handleDelete(row) },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
